import {
  confirmIndustrialCostDirectMaterialEntriesImport,
  previewIndustrialCostDirectMaterialEntriesImport,
} from '../services/industrialCostService';
import type { DirectMaterialEntryImportConfirmResponse, DirectMaterialEntryImportPreviewResponse } from '../types/industrialCost';

export type DirectInputsImportFlowStatus =
  | 'idle'
  | 'loading'
  | 'needs_mapping'
  | 'ready'
  | 'confirming'
  | 'imported'
  | 'imported_with_issues'
  | 'error';

export type DirectInputsImportFlowIssue = {
  kind: 'missing_required_fields' | 'unresolved_material_refs' | 'warnings';
  severity: 'blocking' | 'non_blocking';
  count: number;
  message: string;
};

export type DirectInputsImportFlowSummary = {
  canContinue: boolean;
  blockingIssues: DirectInputsImportFlowIssue[];
  nonBlockingIssues: DirectInputsImportFlowIssue[];
  allIssues: DirectInputsImportFlowIssue[];
  unresolvedMaterialRefsCount: number;
  missingRequiredFieldsCount: number;
};

export type DirectInputsImportFlowState = {
  file: File | null;
  preview: DirectMaterialEntryImportPreviewResponse | null;
  mapping: Record<string, string | null>;
  materialResolution: Record<string, string | null>;
  normalization: {
    sheet_name?: string | null;
    header_row_index?: number | null;
  } | null;
  status: DirectInputsImportFlowStatus;
  error: string | null;
  resultMessage: string | null;
  confirmResult: DirectMaterialEntryImportConfirmResponse | null;
};

export function createDirectInputsImportFlowState(
  file: File | null = null,
): DirectInputsImportFlowState {
  return {
    file,
    preview: null,
    mapping: {},
    materialResolution: {},
    normalization: null,
    status: 'idle',
    error: null,
    resultMessage: null,
    confirmResult: null,
  };
}

function getErrorMessage(error: unknown, fallback: string): string {
  if (typeof error === 'object' && error !== null) {
    const maybeError = error as {
      response?: { data?: { detail?: string } };
      message?: string;
    };

    if (maybeError.response?.data?.detail) {
      return maybeError.response.data.detail;
    }

    if (maybeError.message) {
      return maybeError.message;
    }
  }

  return fallback;
}

export function deriveDirectInputsImportFlowStatus(
  preview: DirectMaterialEntryImportPreviewResponse | null,
): DirectInputsImportFlowStatus {
  if (!preview) {
    return 'idle';
  }

  if ((preview.missing_required_fields?.length ?? 0) > 0) {
    return 'needs_mapping';
  }

  return 'ready';
}

export async function runDirectInputsImportPreview(
  state: DirectInputsImportFlowState,
  overrides?: {
    file?: File | null;
    mapping?: Record<string, string | null>;
    materialResolution?: Record<string, string | null>;
    normalization?: {
      sheet_name?: string | null;
      header_row_index?: number | null;
    } | null;
  },
): Promise<DirectInputsImportFlowState> {
  const file = overrides?.file ?? state.file;
  const mapping = overrides?.mapping ?? state.mapping;
  const materialResolution = overrides?.materialResolution ?? state.materialResolution;
  const normalization = overrides?.normalization ?? state.normalization;

  if (!file) {
    return {
      ...state,
      file: null,
      preview: null,
      normalization: null,
      status: 'idle',
      error: null,
      resultMessage: null,
      confirmResult: null,
    };
  }

  try {
    const preview = await previewIndustrialCostDirectMaterialEntriesImport(
      file,
      mapping,
      materialResolution,
      normalization ?? undefined,
    );

    return {
      ...state,
      file,
      preview,
      mapping: preview.auto_mapping ?? mapping,
      materialResolution,
      normalization,
      status: deriveDirectInputsImportFlowStatus(preview),
      error: null,
      resultMessage: null,
      confirmResult: null,
    };
  } catch (error) {
    return {
      ...state,
      file,
      preview: null,
      normalization,
      status: 'error',
      error: getErrorMessage(error, 'No se pudo leer el archivo de entradas.'),
      resultMessage: null,
      confirmResult: null,
    };
  }
}

export async function applyDirectInputsImportMapping(
  state: DirectInputsImportFlowState,
  field: string,
  column: string,
): Promise<DirectInputsImportFlowState> {
  const nextMapping = {
    ...state.mapping,
    [field]: column || null,
  };

  return runDirectInputsImportPreview(state, {
    mapping: nextMapping,
  });
}

export async function applyDirectInputsMaterialResolution(
  state: DirectInputsImportFlowState,
  sourceValue: string,
  materialId: string,
): Promise<DirectInputsImportFlowState> {
  const nextResolution = {
    ...state.materialResolution,
    [sourceValue]: materialId || null,
  };

  return runDirectInputsImportPreview(state, {
    materialResolution: nextResolution,
  });
}

export function getDirectInputsUnresolvedMaterialRefs(
  state: DirectInputsImportFlowState,
) {
  return state.preview?.detected_material_refs.filter((item) => !item.is_resolved) ?? [];
}

export function getDirectInputsImportSummary(
  state: DirectInputsImportFlowState,
): DirectInputsImportFlowSummary {
  const preview = state.preview;

  if (!preview) {
    return {
      canContinue: false,
      blockingIssues: [],
      nonBlockingIssues: [],
      allIssues: [],
      unresolvedMaterialRefsCount: 0,
      missingRequiredFieldsCount: 0,
    };
  }

  const missingRequiredFieldsCount = preview.missing_required_fields.length;
  const unresolvedMaterialRefsCount = getDirectInputsUnresolvedMaterialRefs(state).length;
  const blockingIssues: DirectInputsImportFlowIssue[] = [];
  const nonBlockingIssues: DirectInputsImportFlowIssue[] = [];

  if (missingRequiredFieldsCount > 0) {
    blockingIssues.push({
      kind: 'missing_required_fields',
      severity: 'blocking',
      count: missingRequiredFieldsCount,
      message: `Faltan ${missingRequiredFieldsCount} campo(s) obligatorio(s) por mapear.`,
    });
  }

  if (unresolvedMaterialRefsCount > 0) {
    nonBlockingIssues.push({
      kind: 'unresolved_material_refs',
      severity: 'non_blocking',
      count: unresolvedMaterialRefsCount,
      message: `Hay ${unresolvedMaterialRefsCount} referencia(s) nuevas de material. PROA las registrará automáticamente al confirmar si sus filas contienen los datos obligatorios válidos.`,
    });
  }

  return {
    canContinue: missingRequiredFieldsCount === 0,
    blockingIssues,
    nonBlockingIssues,
    allIssues: [...blockingIssues, ...nonBlockingIssues],
    unresolvedMaterialRefsCount,
    missingRequiredFieldsCount,
  };
}

export function canContinueDirectInputsImport(
  state: DirectInputsImportFlowState,
): boolean {
  return getDirectInputsImportSummary(state).canContinue;
}

export async function confirmDirectInputsImport(
  state: DirectInputsImportFlowState,
): Promise<DirectInputsImportFlowState> {
  if (!state.file || !state.preview) {
    return state;
  }

  try {
    const result = await confirmIndustrialCostDirectMaterialEntriesImport(
      state.file,
      state.mapping,
      state.materialResolution,
      state.normalization ?? undefined,
    );

    const skipped =
      result.rows_skipped_unresolved_material +
      result.rows_skipped_invalid_date +
      result.rows_skipped_invalid_quantity +
      result.rows_skipped_invalid_unit_price;
    // IND-013: informativo, no son avisos.
    const ignoredTestArticle = result.rows_skipped_test_article ?? 0;
    const ignoredZeroQuantity = result.rows_skipped_zero_quantity ?? 0;
    const returnsReplaced = result.warnings.find(
      (warning) => warning.type === 'documents_with_returns_replaced',
    );

    const skipReasons = [
      result.rows_skipped_unresolved_material > 0
        ? `${result.rows_skipped_unresolved_material} con material sin resolver` : null,
      result.rows_skipped_invalid_date > 0
        ? `${result.rows_skipped_invalid_date} con fecha inválida` : null,
      result.rows_skipped_invalid_quantity > 0
        ? `${result.rows_skipped_invalid_quantity} sin cantidad válida` : null,
      result.rows_skipped_invalid_unit_price > 0
        ? `${result.rows_skipped_invalid_unit_price} sin precio válido` : null,
    ].filter((reason): reason is string => reason !== null);

    const hasIssues =
      skipped > 0 ||
      result.row_errors.length > 0 ||
      result.persistence_errors.length > 0 ||
      result.warnings.length > 0;

    return {
      ...state,
      status: hasIssues ? 'imported_with_issues' : 'imported',
      error: null,
      resultMessage: `Entradas: ${result.rows_imported} nuevas · ${result.rows_skipped_existing} ya registradas` +
        `${skipped > 0 ? ` · ${skipped} omitidas (${skipReasons.join(', ')})` : ''}` +
        `${(result.replaced_documents?.length ?? 0) > 0
          // IND-017: recuento; la lista va en el detalle («Revisa tus ficheros»).
          ? ` · ${result.replaced_documents?.length ?? 0} albaranes sustituidos por el Excel`
          : ''}` +
        `${result.rows_needing_review > 0 ? ` · ${result.rows_needing_review} ambiguas, sin albarán o de otro proveedor (omitidas)` : ''}` +
        `${result.persistence_errors.length > 0 ? ` · ${result.persistence_errors.length} errores al guardar` : ''}` +
        `${ignoredZeroQuantity > 0 ? ` · ${ignoredZeroQuantity} con cantidad 0 (no es un movimiento)` : ''}` +
        `${ignoredTestArticle > 0 ? ` · ${ignoredTestArticle} líneas informativas (9999) ignoradas` : ''}` +
        `${(result.rows_skipped_price_credit ?? 0) > 0 ? ` · ${result.rows_skipped_price_credit} abono de precio, no afecta al stock` : ''}` +
        `${returnsReplaced ? ` · ${String(returnsReplaced.message ?? '').replace(/\.$/, '')}` : ''}.`,
      confirmResult: result,
    };
  } catch (error) {
    return {
      ...state,
      status: 'error',
      error: getErrorMessage(
        error,
        'No se pudo confirmar la importación de entradas.',
      ),
      resultMessage: null,
      confirmResult: null,
    };
  }
}